import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  HostBinding,
  Input,
  Output,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { IMedia, MediaType } from '@epicuro-next/ataraxia/models';
import { ITileLink, PanelParams } from '@epicuro-next/data/merchandise';
import { isPanelStatusEvent } from 'libs/platform/dynamic-components-configs/src/lib/configs/types/panel-event';
import { DynamicCollectionsOutletComponent } from 'libs/platform/dynamic-components/src/lib/components/dynamic-collections-outlet/dynamic-collections-outlet.component';

import { LandingLinkService } from '../service/link.service';

@Component({
  selector: 'epc-tile',
  templateUrl: './tile.component.html',
  styleUrls: ['./tile.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class TileComponent {
  @HostBinding('class.w-100') public get fullWidthHostBinding(): boolean {
    return Boolean(this.dynamicPanel);
  }

  @HostBinding('style.min-height') public get minHeightHostBinding(): string {
    return Boolean(this.dynamicPanel) ? '' : '25rem';
  }

  @HostBinding('style.max-height') public get maxHeightHostBinding(): string {
    return Boolean(this.dynamicPanel) ? '' : '34rem';
  }

  @HostBinding('class.legacy') public get legacyClass(): boolean {
    return !Boolean(this.dynamicPanel);
  }

  @Input()
  public tile: ITileLink;

  @Input()
  public dynamicPanel?: {
    panel: DynamicCollectionsOutletComponent['items'];
    fallbackPanel: DynamicCollectionsOutletComponent['items'];
  };

  @Output()
  public tileClick = new EventEmitter<string>();

  public videoPlayerProps: Partial<HTMLVideoElement> = {
    autoplay: true,
    controls: false,
  };

  private shouldShowFallbackPanel = false;

  constructor(
    private linkService: LandingLinkService,
    private router: ActivatedRoute,
  ) {}

  public get panelDefinition(): DynamicCollectionsOutletComponent['items'] {
    const dynamicPanel = this.dynamicPanel;
    if (dynamicPanel === undefined) {
      throw new Error('Cannot obtain panel definition as it is undefined.');
    }

    return !this.shouldShowFallbackPanel
      ? dynamicPanel.panel
      : dynamicPanel.fallbackPanel;
  }

  public get style() {
    return this.linkService.getLinkStyle(this.tile);
  }

  public get media(): IMedia | null {
    const url = this.tile.video?.url ?? this.tile.image;
    const type = this.tile.video ? MediaType.VIDEO : MediaType.IMAGE;
    if (url) return { url, type };
    else return null;
  }

  public get routeProps() {
    return this.linkService.getAnchorProps(
      this.tile,
      this.router.snapshot.params.siteId,
    );
  }

  public get isClickable() {
    return Boolean(this.routeProps.routerLink || this.routeProps.href);
  }

  public onTileClick() {
    this.tileClick.emit(this.linkService.anchorPropsToHref(this.routeProps));
  }

  public onEvent(panelEvent: unknown): void {
    if (isPanelStatusEvent(panelEvent)) {
      if (
        !this.shouldShowFallbackPanel &&
        (panelEvent.data === 'hasNoData' || panelEvent.data === 'hasError') &&
        Boolean(this.dynamicPanel?.fallbackPanel)
      ) {
        this.shouldShowFallbackPanel = true;

        const panelParams = this.tile.params as PanelParams;
        const panelNameBefore = panelParams?.panelName ?? 'N/A';
        const fallbackPanelName =
          panelParams?.fallbackPanel?.panelName ?? 'N/A';

        console.warn(
          `Panel '${panelNameBefore}' switched to fallback panel '${fallbackPanelName}' because of '${panelEvent.data}'.`,
        );
      }
    } else {
      console.warn(
        'A tile received a panel event for which it has no handler defined.',
        panelEvent,
      );
    }
  }
}
